import * as React from "react";
import Modal from "../../../ui/modal/Modal";
import ButtonTertiary from "../../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import ButtonPrimary from "../../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import QuoteEmailTemplate from "../../../../model/partnerBrand/QuoteEmailTemplate";
import {none, option, Option} from "ts-option";
import {SelectTemplate} from "./SelectTemplate";
import Input from "../../../../hiberUiKit/src/atoms/Input/Input";
import FormGroup from "../../../../hiberUiKit/src/molecules/FormGroup/FormGroup";
import CKEditor from "react-ckeditor-component";
import ConfirmDeleteTemplateModal from "./ConfirmDeleteTemplateModal";
import {api} from "../../../../api/api";
import Toast from "../../../helper/Toast";
import Loading from "../../../ui/loading/Loading";
import {notifyBugsnagAndShowToast} from "../../../../utils/Utils";
import Configs from "../../../common/Configs";

interface IProps {
	/**
	 * On close callback
	 */
	onClose: () => void
	/**
	 * Partner brand Id, needed to get customer email to send message to.
	 */
	partnerBrandId: string
}

interface IState {
	/**
	 * Already saved templates for this partner brand.
	 */
	availableTemplates: Option<QuoteEmailTemplate[]>;
	/**
	 * Html content.
	 */
	inputValue: string;
	/**
	 * Existing template selected.
	 */
	selectedTemplate: Option<QuoteEmailTemplate>;
	/**
	 * Name of new template to be saved.
	 */
	newTemplateName: Option<string>;
	/**
	 * Indicates if template delete confirmation modal is visible.
	 */
	deleteConfirmationModalVisible: boolean;
	/**
	 * Indicates if template data is loading.
	 */
	isLoading: boolean;
}

export default class EditQuoteEmailTemplatesModal extends React.Component<IProps, IState> {
	editorConfig = Configs.createEditorConfig(400);

	constructor(props: IProps) {
		super(props);
		this.state = {
			availableTemplates: none,
			inputValue: "",
			selectedTemplate: none,
			newTemplateName: none,
			deleteConfirmationModalVisible: false,
			isLoading: true
		};
	}

	componentDidMount() {
		api.instance.partnerBrandApi.fetchQuoteEmailTemplates(this.props.partnerBrandId)
			.then(templates => this.setState({availableTemplates: option(templates), isLoading: false}))
			.catch((e) => {
				this.props.onClose();
				notifyBugsnagAndShowToast(e, "Fetch templates");
			});
	}

	render() {
		return (
			<Modal onCloseClicked={this.props.onClose} title={"Create quote e-mail templates"} fixedWidth={true}>
				<Loading isLoading={this.state.isLoading}>
					<div className="c-quote-email-modal">
						<div style={{paddingBottom: "25px"}}>
							<div style={{width: "30%", display: "inline-block"}}>
								<FormGroup label={"Load existing template"}>
									<SelectTemplate items={this.state.availableTemplates}
													selectedId={this.state.selectedTemplate.map(t => t.id)}
													onChanged={this.onTemplateSelect}
													placeholder={"Select template..."}/>
								</FormGroup>
							</div>
							<div style={{display: "inline-block", marginLeft: "30px"}}>
								<ButtonPrimary label={"Delete template"} disabled={this.state.selectedTemplate.isEmpty}
											   onClick={() => this.setState({deleteConfirmationModalVisible: true})}/>
							</div>

							<div style={{width: "30%", display: "inline-block", marginLeft: "120px"}}>
								<FormGroup label={"Name of template to be saved"}>
									<Input type={"text"}
										   value={this.state.newTemplateName.getOrElse("")}
										   placeholder={"Enter name of new template (required)"}
										   onChange={this.onNewTemplateNameChange}/>
								</FormGroup>
							</div>
						</div>
						<CKEditor content={this.state.inputValue}
								  config={this.editorConfig}
								  events={{
									  "change": this.onContentChange
								  }}/>
						<div className="c-quote-email-modal_buttons-bar">
							<ButtonTertiary label={"Cancel"} onClick={this.props.onClose}/>
							<ButtonPrimary label={"Save template"}
										   disabled={!this.state.newTemplateName.getOrElse("").length}
										   onClick={this.onCreateTemplate}/>
						</div>
					</div>
					{this.state.deleteConfirmationModalVisible &&
					<ConfirmDeleteTemplateModal templateName={this.state.selectedTemplate.map(t => t.name).orNull}
												onClose={() => this.setState({deleteConfirmationModalVisible: false})}
												onConfirm={this.onDeleteTemplate}/>}
				</Loading>
			</Modal>
		);
	}

	onContentChange = (evt: any) => {
		this.setState({inputValue: evt.editor.getData()});
	};

	/**
	 * Callback on template select.
	 * @param data
	 */
	onTemplateSelect = (data: Option<QuoteEmailTemplate>) => {
		data.map(template => this.setState({selectedTemplate: option(template), inputValue: template.content}));
	};

	onCreateTemplate = () => {
		const template = QuoteEmailTemplate.new(this.state.newTemplateName.getOrElse(""), this.state.inputValue);
		api.instance.partnerBrandApi.createQuoteEmailTemplate(template.toCreateJson(), this.props.partnerBrandId).then(() => {
			this.props.onClose();
			Toast.success("Template created successfully", "Create template");
		}).catch(e => notifyBugsnagAndShowToast(e, "Create template"));
	};

	onDeleteTemplate = () => {
		this.state.selectedTemplate.map(t => {
			api.instance.partnerBrandApi.removeQuoteEmailTemplate(t.id).then(() => {
				this.setState({
					selectedTemplate: none,
					inputValue: "",
					availableTemplates: option(
						this.state.availableTemplates.getOrElse([]).filter(template => template.id !== t.id))
				});
				Toast.success("Template deleted successfully", "Delete template")
			}).catch(e => notifyBugsnagAndShowToast(e, "Delete template"));
		});
	};

	/**
	 * Template name change callback.
	 * @param {React.ChangeEvent<HTMLInputElement>} evt
	 */
	onNewTemplateNameChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({newTemplateName: option(evt.target.value)});
	};
}
